/**
 * useScrollTrigger
 * 當元素滾動進入視口時觸發 callback，可自訂是否只觸發一次
 *
 * @param {Ref<HTMLElement>} targetRef - 要觀察的 DOM 元素
 * @param {Function} callback - 進入畫面時要執行的函式
 * @param {Object} options - 可選參數，如 { once: true, threshold: 0.3, enabled: ref(true) 或 boolean }
 */

export function useScrollTrigger ( targetRef, callback, options = {} ) {
	let observer;
	let hasStarted = false;

	const threshold = options.threshold ?? 0.1;
	const once = options.once !== false;
	const enabled = options.enabled ?? true;

	// 啟用觀察器的函式
	const setupObserver = () => {
		if ( !targetRef.value || hasStarted ) return;
		hasStarted = true;

		// 建立觀察器
		observer = new IntersectionObserver( ( entries ) => {
			entries.forEach( ( entry ) => {
				// 判斷是否進入畫面（例如進入 10% 高度）
				if ( entry.isIntersecting ) {
					callback( entry.target ); // 執行傳入的 callback

					// 如果沒設 once: false，就只觸發一次
					if ( once ) {
						observer.unobserve( entry.target );
					}
				}
			} );
		}, {
			threshold,
		} );

		// 開始觀察目標元素
		observer.observe( targetRef.value );
	};

	if ( typeof enabled === 'boolean' ) {
		if ( enabled ) setupObserver();
	} else {
		// 如果 enabled 是 reactive（ref）
		watch(
			enabled,
			( val ) => {
				if ( val ) setupObserver();
			},
			{ immediate: true }
		);
	}

	// 元件卸載時清除觀察器
	onBeforeUnmount( () => {
		observer?.disconnect();
	} );
}
